import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "@carbon/icons-react";
import { Button } from "../../atom/Button";
import { OMSpinner } from "../../atom/Spinner";
import { TRANSLATION_CONFIG } from "../../../utils/consts/translation";
import { ROUTES } from "../../../utils/consts/routes";
import { useAuth } from "../../../utils/contexts/AuthProvider";
import { useUserContext } from "../../../utils/contexts/UserProvider";

const parseUserId = (user: string | null): string | undefined => {
  try {
    return user ? JSON.parse(user)?.id : undefined;
  } catch {
    return undefined;
  }
};

// Landing page for super-admin's "Impersonate" panel, which opens
// /impersonate-user?token=<access_token>&user=<JSON user>
export const ImpersonateUser = () => {
  const { t } = useTranslation(TRANSLATION_CONFIG.appNamespace);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const { setUser } = useUserContext();
  const token = params.get("token");
  const userId = parseUserId(params.get("user"));

  useEffect(() => {
    if (!token || !userId) {
      return;
    }

    // Drop any existing session first, otherwise its refresh token would be used
    // to refresh and replace the impersonation token
    setUser(null);
    logout();

    // No refresh token or expiry is stored, so the impersonation session ends on the first 401
    localStorage.setItem("userid", userId);
    login(token);
    navigate(ROUTES.dashboard, { replace: true });
  }, []);

  if (token && userId) {
    return <OMSpinner />;
  }

  return (
    <div className="flex items-center justify-center bg-white">
      <div className="flex flex-col justify-center items-center min-w-[360px] max-w-[360px] mt-24 gap-8">
        <img src="/images/logo.svg" className="h-[24px]" alt="" />
        <p className="font-light text-[16px] leading-[22.4px] tracking-[0.2px] text-gray-550 text-center">
          {t("Please login before trying to impersonate user")}
        </p>
        <div className="w-full">
          <Button
            type="primary"
            label={t("Log in")}
            onClick={() => navigate(ROUTES.home)}
            leftIcon={<ArrowRight size={20} />}
            additionalClassName="flex items-center justify-center w-full"
          />
        </div>
      </div>
    </div>
  );
};
